/**
 * lib/edge-functions.ts
 * =====================
 * Replacement for `supabase.functions.invoke(...)`.
 *
 * Edge functions now run behind an AWS Lambda function URL with the same
 * path shape as Supabase: `${EDGE_FUNCTIONS_URL}/functions/v1/<name>`.
 *
 * Auth:
 *  - Server-side callers (SSR / server functions) authenticate with the
 *    internal shared secret: `Authorization: Bearer ${EDGE_INTERNAL_SECRET}`.
 *  - Browser callers attach the Cognito access token (fetched from
 *    /api/auth/token); the edge functions verify it as a Cognito JWT.
 *
 * The return shape mirrors supabase.functions.invoke: `{ data, error }`,
 * with `error` non-null on a non-2xx response.
 */

import { createIsomorphicFn } from "@tanstack/react-start";

export interface EdgeFunctionResult<T = unknown> {
  data: T | null;
  error: Error | null;
}

function getBaseUrl(): string | undefined {
  if (typeof window === "undefined") {
    return (
      process.env.EDGE_FUNCTIONS_URL ??
      (import.meta.env?.VITE_EDGE_FUNCTIONS_URL as string | undefined)
    );
  }
  return import.meta.env.VITE_EDGE_FUNCTIONS_URL as string | undefined;
}

/** Resolve the Authorization header value for the current environment. */
const resolveAuthHeader = createIsomorphicFn()
  .server(async (): Promise<string | null> => {
    // Server → edge: internal shared secret.
    const secret = process.env.EDGE_INTERNAL_SECRET;
    return secret ? `Bearer ${secret}` : null;
  })
  .client(async (): Promise<string | null> => {
    // Browser → edge: the Cognito access token. It lives in an httpOnly cookie,
    // so we fetch it from the same-origin /api/auth/token endpoint (which
    // refreshes it if expired). The edge functions verify it as a Cognito JWT.
    try {
      const res = await fetch("/api/auth/token", { credentials: "same-origin" });
      if (!res.ok) return null;
      const { token } = (await res.json()) as { token: string | null };
      return token ? `Bearer ${token}` : null;
    } catch {
      return null;
    }
  });

export async function invokeEdgeFunction<T = unknown>(
  name: string,
  body: unknown,
  opts?: { headers?: Record<string, string> },
): Promise<EdgeFunctionResult<T>> {
  try {
    const baseUrl = getBaseUrl();
    if (!baseUrl) {
      return {
        data: null,
        error: new Error(
          "Edge functions URL is not configured (EDGE_FUNCTIONS_URL / VITE_EDGE_FUNCTIONS_URL)",
        ),
      };
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(opts?.headers ?? {}),
    };

    const hasAuth = Object.keys(headers).some(
      (k) => k.toLowerCase() === "authorization",
    );
    if (!hasAuth) {
      const auth = await resolveAuthHeader();
      if (auth) headers["Authorization"] = auth;
    }

    const res = await fetch(
      `${baseUrl.replace(/\/$/, "")}/functions/v1/${name}`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(body ?? {}),
      },
    );

    const raw = await res.text();
    let parsed: unknown = null;
    if (raw) {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = raw;
      }
    }

    if (!res.ok) {
      const message =
        (parsed && typeof parsed === "object" && "error" in (parsed as any)
          ? String((parsed as any).error)
          : typeof parsed === "string" && parsed
            ? parsed
            : `Edge function ${name} failed with status ${res.status}`);
      return { data: null, error: new Error(message) };
    }

    return { data: parsed as T, error: null };
  } catch (e: any) {
    return {
      data: null,
      error: e instanceof Error ? e : new Error(String(e)),
    };
  }
}
